import { clientFor, type PageQuery, type PageResult } from '@yc/api-client'

import type { Charge } from './charges'

const api = clientFor('/ops/api')

/** 运输订单状态：draft → confirmed → in_transit → signed；draft/confirmed → cancelled */
export type OrderStatus = 'draft' | 'confirmed' | 'in_transit' | 'signed' | 'cancelled'

/** 单据来源：手工建单或 CSV 导入 */
export type OrderSource = 'manual' | 'import'

/** 可在页面上触发的订单动作，与后端 POST /orders/:id/<action> 一一对应 */
export type OrderAction = 'confirm' | 'cancel' | 'transit' | 'sign' | 'charge'

/** 运输订单。字段与 services/ops/internal/order/model.go 的 Order 一一对应。 */
export interface Order {
  id: number
  tenant_id: number
  doc_no: string
  customer_id: number
  consignee: string
  consignee_phone: string
  origin: string
  dest: string
  expect_pickup_at: string | null
  expect_deliver_at: string | null
  distance_km: number
  weight_kg: number
  volume_cm3: number
  cargo_desc: string
  status: OrderStatus
  source: OrderSource
  remark: string
  created_by: string
  created_at: string
  updated_at: string
}

/** 货品明细行：同名行在后端写入前自动合并 */
export interface OrderItem {
  id: number
  tenant_id: number
  order_id: number
  name: string
  qty: number
  unit: string
  weight_kg: number
  volume_cm3: number
  created_at: string
  updated_at: string
}

/** 列表行：单据 + 客户名 + 明细数量合计 + 有效计费笔数 */
export interface ListRow extends Order {
  customer_name: string
  total_qty: number
  charge_count: number
}

export interface OrderDetail {
  order: Order
  items: OrderItem[]
  customer_name: string
}

export interface OrderItemInput {
  name: string
  qty: number
  unit?: string
  weight_kg?: number
  volume_cm3?: number
}

export interface OrderInput {
  customer_id: number
  consignee: string
  consignee_phone: string
  origin: string
  dest: string
  expect_pickup_at?: string | null
  expect_deliver_at?: string | null
  distance_km: number
  weight_kg: number
  volume_cm3: number
  cargo_desc: string
  remark: string
  items: OrderItemInput[]
}

export interface OrderQuery extends PageQuery {
  keyword?: string
  customer?: number
  status?: string
  source?: string
}

/** 与后端 services/ops/internal/order/model.go 的 StatusNames 同源两份，改一边必须改另一边 */
export const STATUS_LABELS: Record<OrderStatus, string> = {
  draft: '草稿',
  confirmed: '已确认',
  in_transit: '在途',
  signed: '已签收',
  cancelled: '已取消',
}

/** 与后端 services/ops/internal/order/model.go 的 SourceNames 同源两份 */
export const SOURCE_LABELS: Record<OrderSource, string> = {
  manual: '手工',
  import: 'CSV 导入',
}

/** 状态 → 表格里的标签色 */
export const STATUS_TAG: Record<OrderStatus, 'info' | 'primary' | 'warning' | 'success' | 'danger'> = {
  draft: 'info',
  confirmed: 'primary',
  in_transit: 'warning',
  signed: 'success',
  cancelled: 'danger',
}

/**
 * 各状态下允许的动作，抄后端 internal/order/service.go 的状态机：
 * 确认 draft→confirmed；在途 confirmed→in_transit；签收 confirmed/in_transit→signed；
 * 取消 draft/confirmed→cancelled；计费只对 signed。
 */
const ACTIONS: Record<OrderStatus, OrderAction[]> = {
  draft: ['confirm', 'cancel'],
  confirmed: ['transit', 'sign', 'cancel'],
  in_transit: ['sign'],
  signed: ['charge'],
  cancelled: [],
}

/** 状态能不能触发某个动作（前端只做展示，最终以后端 409 为准） */
export function canAct(status: OrderStatus, action: OrderAction): boolean {
  return ACTIONS[status].includes(action)
}

export const listOrders = (q: OrderQuery): Promise<PageResult<ListRow>> =>
  api.get<PageResult<ListRow>>('/orders', { ...q })

export const getOrder = (id: number): Promise<OrderDetail> => api.get<OrderDetail>(`/orders/${id}`)

export const createOrder = (input: OrderInput): Promise<Order> => api.post<Order>('/orders', input)

export const updateOrder = (id: number, input: OrderInput): Promise<Order> =>
  api.put<Order>(`/orders/${id}`, input)

/** 删除仅限草稿；后端用 ops:order:update 做删除鉴权（yaml 里没有单独的 delete 码） */
export const deleteOrder = (id: number): Promise<void> => api.del<void>(`/orders/${id}`)

const act = (id: number, action: Exclude<OrderAction, 'charge'>): Promise<void> =>
  api.post<void>(`/orders/${id}/${action}`, {})

export const confirmOrder = (id: number): Promise<void> => act(id, 'confirm')
export const cancelOrder = (id: number): Promise<void> => act(id, 'cancel')
export const transitOrder = (id: number): Promise<void> => act(id, 'transit')
export const signOrder = (id: number): Promise<void> => act(id, 'sign')

/** 计费：rate_card_id 不传时后端只在「恰有一条启用规则」下自动挑，否则 422 要求指定 */
export const chargeOrder = (id: number, rateCardId?: number): Promise<Charge> =>
  api.post<Charge>(`/orders/${id}/charge`, rateCardId ? { rate_card_id: rateCardId } : {})
